import React, { useState, useEffect } from 'react';
import {
  Calendar, ChevronLeft, ChevronRight, Clock, ChevronDown, ChevronUp,
  Settings, Save, Copy, Download, Plus, X
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import HistoriqueModal from '../components/HistoriqueModal';
import './EmploiPedagogique.css';

const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const JOURS_ABR = ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'];

const EmploiPedagogique = () => {
  const [creneauxGlobaux, setCreneauxGlobaux] = useState({});
  const [creneauxSpecifiques, setCreneauxSpecifiques] = useState({});

  // États pour le panneau global
  const [showGlobalPanel, setShowGlobalPanel] = useState(false);
  const [globalTemp, setGlobalTemp] = useState({});
  const [globalJourActif, setGlobalJourActif] = useState('Lundi');

  // États pour le panneau spécifique
  const [showCoursPanel, setShowCoursPanel] = useState(null); // coursId
  const [coursTemp, setCoursTemp] = useState({});
  const [coursJourActif, setCoursJourActif] = useState('Lundi');

  const [coursList, setCoursList] = useState([]);
  const [profList, setProfList] = useState([]);
  const [selectedCours, setSelectedCours] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [emploiDuTemps, setEmploiDuTemps] = useState({});
  const [copyLoading, setCopyLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const [userInfo, setUserInfo] = useState(null);
  const [permissions, setPermissions] = useState({ canModify: false, canCreate: false, canDelete: false, filiere: null, isGeneral: false });
  const [showHistorique, setShowHistorique] = useState(false);
  const [selectedSeanceForHistory, setSelectedSeanceForHistory] = useState(null);

  useEffect(() => {
    const sg = localStorage.getItem('creneauxGlobaux');
    const ss = localStorage.getItem('creneauxSpecifiques');
    if (sg) { try { setCreneauxGlobaux(JSON.parse(sg)); } catch (e) { } }
    if (ss) { try { setCreneauxSpecifiques(JSON.parse(ss)); } catch (e) { } }
  }, []);

  const showMsg = (type, text, delay = 3000) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), delay);
  };

  const fmt = (t) => { if (!t) return ''; const [h, m] = t.split(':'); return `${parseInt(h)}h${m !== '00' ? m : ''}`; };
  const label = (d, f) => d && f ? `${fmt(d)}–${fmt(f)}` : '';

  // ── Ouvrir/fermer panneau global ──
  const openGlobalPanel = () => {
    const t = {};
    JOURS.forEach(j => { t[j] = creneauxGlobaux[j] ? [...creneauxGlobaux[j]] : []; });
    setGlobalTemp(t);
    setGlobalJourActif('Lundi');
    setShowGlobalPanel(true);
  };

  const closeGlobalPanel = () => {
    setShowGlobalPanel(false);
    setGlobalTemp({});
  };

  const saveGlobalPanel = () => {
    const trie = {};
    JOURS.forEach(j => { trie[j] = [...(globalTemp[j] || [])].sort((a, b) => a.debut.localeCompare(b.debut)); });
    localStorage.setItem('creneauxGlobaux', JSON.stringify(trie));
    setCreneauxGlobaux(trie);
    closeGlobalPanel();
    showMsg('success', 'Créneaux globaux sauvegardés');
  };

  const addGlobal = () => {
    setGlobalTemp(prev => ({
      ...prev,
      [globalJourActif]: [...(prev[globalJourActif] || []), { id: Date.now(), debut: '08:00', fin: '10:00' }]
    }));
  };

  const removeGlobal = (id) => {
    setGlobalTemp(prev => ({
      ...prev,
      [globalJourActif]: (prev[globalJourActif] || []).filter(c => c.id !== id)
    }));
  };

  const updateGlobal = (id, field, val) => {
    setGlobalTemp(prev => ({
      ...prev,
      [globalJourActif]: (prev[globalJourActif] || []).map(c => c.id === id ? { ...c, [field]: val } : c)
    }));
  };

  // ── Ouvrir/fermer panneau spécifique ──
  const openCoursPanel = (coursId) => {
    const t = {};
    JOURS.forEach(j => {
      t[j] = creneauxSpecifiques[coursId]?.[j]
        ? [...creneauxSpecifiques[coursId][j]]
        : (creneauxGlobaux[j] ? [...creneauxGlobaux[j]] : []);
    });
    setCoursTemp(t);
    setCoursJourActif('Lundi');
    setShowCoursPanel(coursId);
  };

  const closeCoursPanel = () => {
    setShowCoursPanel(null);
    setCoursTemp({});
  };

  const saveCoursPanel = () => {
    const trie = {};
    JOURS.forEach(j => { trie[j] = [...(coursTemp[j] || [])].sort((a, b) => a.debut.localeCompare(b.debut)); });
    const newSpec = { ...creneauxSpecifiques, [showCoursPanel]: trie };
    localStorage.setItem('creneauxSpecifiques', JSON.stringify(newSpec));
    setCreneauxSpecifiques(newSpec);
    const cours = coursList.find(c => c._id === showCoursPanel);
    closeCoursPanel();
    showMsg('success', `Créneaux sauvegardés pour ${cours?.nom}`);
  };

  const addCours = () => {
    setCoursTemp(prev => ({
      ...prev,
      [coursJourActif]: [...(prev[coursJourActif] || []), { id: Date.now(), debut: '08:00', fin: '10:00' }]
    }));
  };

  const removeCours = (id) => {
    setCoursTemp(prev => ({
      ...prev,
      [coursJourActif]: (prev[coursJourActif] || []).filter(c => c.id !== id)
    }));
  };

  const updateCours = (id, field, val) => {
    setCoursTemp(prev => ({
      ...prev,
      [coursJourActif]: (prev[coursJourActif] || []).map(c => c.id === id ? { ...c, [field]: val } : c)
    }));
  };

  const getCreneauxPourCours = (coursId) => creneauxSpecifiques[coursId] || creneauxGlobaux;

  // ── Semaine ──
  const getWeekDates = (date) => {
    const start = new Date(date);
    const day = start.getDay();
    start.setDate(start.getDate() - day + (day === 0 ? -6 : 1));
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  };
  const weekDates = getWeekDates(currentWeek);
  const formatDate = (d) => d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  const changeWeek = (dir) => { const d = new Date(currentWeek); d.setDate(d.getDate() + dir * 7); setCurrentWeek(d); };

  // ── API ──
  useEffect(() => { fetchUserInfo(); }, []);
  useEffect(() => { if (userInfo) fetchData(); }, [userInfo]);
  useEffect(() => { if (coursList.length > 0 && selectedCours.length === 0) setSelectedCours([coursList[0]._id]); }, [coursList.length]);
  useEffect(() => { if (coursList.length > 0 && userInfo) fetchSeancesReelles(); }, [currentWeek, coursList.length, userInfo]);

  const fetchUserInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('    https://vmi1977988.contaboserver.net/api2/auth/me', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const u = await res.json();
        setUserInfo(u);
        const isGeneral = u.filiere === 'GENERAL' || u.role === 'pedagogique_general';
        setPermissions({ canModify: true, canCreate: true, canDelete: true, filiere: u.filiere, isGeneral });
      }
    } catch (err) { console.error(err); }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const coursUrl = permissions.isGeneral ? '    https://vmi1977988.contaboserver.net/api2/cours' : '    https://vmi1977988.contaboserver.net/api2/pedagogique/cours';
      const rc = await fetch(coursUrl, { headers: { Authorization: `Bearer ${token}` } });
      if (rc.ok) setCoursList(await rc.json());
      const rp = await fetch('    https://vmi1977988.contaboserver.net/api2/professeurs', { headers: { Authorization: `Bearer ${token}` } });
      if (rp.ok) { const d = await rp.json(); setProfList(d.filter(p => p.actif)); }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchSeancesReelles = async () => {
    try {
      const token = localStorage.getItem('token');
      const d = weekDates[0];
      const lundi = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const res = await fetch(`     https://vmi1977988.contaboserver.net/api2/seances/semaine/${lundi}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); if (coursList.length > 0) organiserSeances(data); }
    } catch (err) { console.error(err); }
  };

  const organiserSeances = (seancesData) => {
    const emploi = {};
    seancesData.forEach(seance => {
      const coursObj = coursList.find(c => c._id === seance.coursId) || coursList.find(c => c._id === seance.cours) || coursList.find(c => c.nom === seance.cours);
      if (!coursObj) return;
      const coursId = coursObj._id;
      const jourKey = getJourForSeance(seance) || 'Lundi';
      const hDeb = normalizeTime(seance.heureDebut);
      const hFin = normalizeTime(seance.heureFin);
      const key = `${jourKey}-${hDeb}-${hFin}`;
      if (!emploi[coursId]) emploi[coursId] = {};
      emploi[coursId][key] = {
        professeur: seance.professeur?._id || seance.professeur,
        matiere: seance.matiere || '', salle: seance.salle || '',
        seanceId: seance._id, typeSeance: seance.typeSeance || 'reelle', actif: seance.actif !== false
      };
    });
    setEmploiDuTemps(emploi);
  };

  const normalizeTime = (t) => {
    if (!t) return '';
    const p = t.toString().split(':');
    return `${(p[0] || '00').padStart(2, '0')}:${(p[1] || '00').padStart(2, '0')}`;
  };

  const getJourForSeance = (seance) => {
    if (seance?.dateSeance) { const d = new Date(seance.dateSeance); return JOURS[(d.getDay() + 6) % 7]; }
    return seance?.jour;
  };

  const getProfesseursPourCours = (coursId) => {
    const cours = coursList.find(c => c._id === coursId);
    if (!cours) return [];
    return profList.filter(prof => {
      if (!prof.actif) return false;
      if (prof.coursEnseignes?.length > 0) return prof.coursEnseignes.some(e => e.nomCours === cours.nom);
      if (prof.cours && Array.isArray(prof.cours)) return prof.cours.includes(cours.nom);
      return false;
    });
  };

  const getMatieresPourProf = (profId, coursId) => {
    const prof = profList.find(p => p._id === profId);
    const cours = coursList.find(c => c._id === coursId);
    if (!prof || !cours) return [];
    if (prof.coursEnseignes?.length > 0)
      return prof.coursEnseignes.filter(e => e.nomCours === cours.nom).map(e => e.matiere);
    if (prof.matiere && prof.cours?.includes(cours.nom)) return [prof.matiere];
    return [];
  };

  const updateCase = (coursId, jour, creneau, field, value) => {
    const key = `${jour}-${creneau.debut}-${creneau.fin}`;
    setEmploiDuTemps(prev => {
      const ns = { ...prev, [coursId]: { ...prev[coursId], [key]: { ...prev[coursId]?.[key], [field]: value } } };
      if (field === 'professeur' && value) {
        const mats = getMatieresPourProf(value, coursId);
        ns[coursId][key].matiere = mats.length === 1 ? mats[0] : '';
      }
      if (field === 'professeur' && !value) ns[coursId][key].matiere = '';
      return ns;
    });
  };

  const startEdit = (coursId, jour, creneau) => {
    const key = `${jour}-${creneau.debut}-${creneau.fin}`;
    setEditing({ coursId, key });
    if (!emploiDuTemps[coursId]?.[key]) {
      setEmploiDuTemps(prev => ({
        ...prev,
        [coursId]: { ...prev[coursId], [key]: { professeur: '', matiere: '', salle: '', seanceId: null, typeSeance: 'nouvelle', actif: true } }
      }));
    }
  };
  const cancelEdit = () => setEditing(null);

  const saveEdit = async (coursId, jour, creneau) => {
    try {
      const key = `${jour}-${creneau.debut}-${creneau.fin}`;
      const s = emploiDuTemps[coursId]?.[key] || {};
      if (!s.professeur) { showMsg('error', 'Sélectionnez un professeur'); return; }
      if (!s.matiere) { showMsg('error', 'Sélectionnez une matière'); return; }
      const token = localStorage.getItem('token');
      const idx = JOURS.indexOf(jour);
      const dd = new Date(weekDates[idx]);
      const dateISO = `${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(2, '0')}-${String(dd.getDate()).padStart(2, '0')}`;
      const payload = { cours: coursId, professeur: s.professeur, matiere: s.matiere, salle: s.salle || '', dateSeance: dateISO, jour, heureDebut: creneau.debut, heureFin: creneau.fin };
      let res;
      if (s.typeSeance === 'exception' && s.seanceId)
        res = await fetch(`     https://vmi1977988.contaboserver.net/api2/seances/${s.seanceId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(payload) });
      else
        res = await fetch('    https://vmi1977988.contaboserver.net/api2/seances/exception', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(payload) });
      if (!res.ok) { const err = await res.json(); showMsg('error', `Erreur: ${err.error}`); return; }
      showMsg('success', 'Séance sauvegardée');
      setEditing(null);
      await fetchSeancesReelles();
    } catch { showMsg('error', 'Erreur'); }
  };

  const deleteSeance = async (coursId, jour, creneau, seanceData) => {
    if (!seanceData.seanceId || !window.confirm('Supprimer cette séance ?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`     https://vmi1977988.contaboserver.net/api2/seances/${seanceData.seanceId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { showMsg('success', 'Séance supprimée'); await fetchSeancesReelles(); }
    } catch { showMsg('error', 'Erreur'); }
  };

  const copierSemainePrecedente = async () => {
    if (!window.confirm('Copier les séances de la semaine précédente ?')) return;
    setCopyLoading(true);
    try {
      const token = localStorage.getItem('token');
      const lundiActuel = weekDates[0].toISOString().split('T')[0];
      const res = await fetch('    https://vmi1977988.contaboserver.net/api2/seances/copier-semaine-precedente', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ lundiDestination: lundiActuel })
      });
      const result = await res.json();
      if (result.ok || res.ok) { showMsg('success', result.message || 'Séances copiées'); await fetchSeancesReelles(); }
      else showMsg('error', result.error || 'Erreur');
    } catch { showMsg('error', 'Erreur de connexion'); }
    finally { setCopyLoading(false); }
  };

  const telechargerPDF = () => {
    if (selectedCours.length === 0) { showMsg('error', 'Sélectionnez une classe'); return; }
    setPdfLoading(true);
    const dateRange = `${formatDate(weekDates[0])} au ${formatDate(weekDates[6])}`;
    const now = new Date().toLocaleDateString('fr-FR');
    let pagesHtml = '';

    selectedCours.forEach(coursId => {
      const cours = coursList.find(c => c._id === coursId);
      if (!cours) return;
      const creneauxDuCours = getCreneauxPourCours(coursId);
      const tousLesCreneaux = [];
      Object.values(creneauxDuCours).forEach(arr => arr.forEach(c => {
        if (!tousLesCreneaux.find(x => x.debut === c.debut && x.fin === c.fin)) tousLesCreneaux.push({ ...c });
      }));
      tousLesCreneaux.sort((a, b) => a.debut.localeCompare(b.debut));
      if (tousLesCreneaux.length === 0) return;

      let rowsHtml = '';
      tousLesCreneaux.forEach(creneau => {
        let cells = '';
        JOURS.forEach(jour => {
          const key = `${jour}-${creneau.debut}-${creneau.fin}`;
          const sd = emploiDuTemps[coursId]?.[key];
          const crEx = creneauxDuCours[jour]?.some(c => c.debut === creneau.debut && c.fin === creneau.fin);
          const aS = sd && sd.actif !== false && sd.professeur;
          const prof = profList.find(p => p._id === sd?.professeur);
          if (aS) {
            cells += `<td class="cs"><div class="cm">${sd.matiere || ''}</div>${sd.salle ? `<div class="cv">${sd.salle}</div>` : ''}<div class="cp">${prof?.nom || ''}</div></td>`;
          } else if (crEx) {
            cells += `<td class="ce"></td>`;
          } else {
            cells += `<td class="cd"></td>`;
          }
        });
        rowsHtml += `<tr><td class="ct"><div class="ctm">${label(creneau.debut, creneau.fin)}</div><div class="cts">${creneau.debut} - ${creneau.fin}</div></td>${cells}</tr>`;
      });

      pagesHtml += `<div class="page"><div class="top"><span class="td">${now}</span><span class="tl">Emploi du Temps</span></div>
      <table><thead><tr><th class="th0"></th>${JOURS_ABR.map(j => `<th>${j}</th>`).join('')}</tr></thead>
      <tbody>${rowsHtml}</tbody></table>
      <div class="bot"><span class="bc">${cours.nom}${cours.filiere ? ' — ' + cours.filiere : ''}</span><span class="bw">Semaine du ${dateRange}</span></div></div>`;
    });

    const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"/>
    <style>*{box-sizing:border-box;margin:0;padding:0;}body{font-family:Arial,sans-serif;}
    .page{width:297mm;min-height:205mm;padding:8mm 10mm;display:flex;flex-direction:column;gap:4mm;page-break-after:always;}
    .page:last-child{page-break-after:avoid;}
    .top{display:flex;justify-content:space-between;border-bottom:2px solid #1a3a6b;padding-bottom:3mm;font-size:8pt;}
    .td{color:#777;}.tl{font-weight:bold;color:#1a3a6b;font-size:11pt;}
    table{width:100%;border-collapse:collapse;flex:1;font-size:8pt;}
    thead tr{background:#1a3a6b;color:#fff;}
    th{padding:6px 4px;text-align:center;border:1px solid #fff;font-size:10pt;}
    .th0{width:26mm;background:#1a3a6b;}
    .ct{background:#dce6f5;text-align:center;vertical-align:middle;padding:4px;border:1px solid #b0bdd4;width:26mm;}
    .ctm{font-weight:bold;font-size:8pt;color:#1a3a6b;}.cts{font-size:7pt;color:#666;}
    .cs{vertical-align:top;padding:4px 5px;border:1px solid #b0bdd4;background:#fff;}
    .cm{font-weight:bold;font-size:7.5pt;margin-bottom:2px;}
    .cv{font-size:7pt;color:#1a3a6b;margin-bottom:2px;}.cp{font-size:7pt;color:#555;font-style:italic;}
    .ce{border:1px solid #b0bdd4;background:#fff;}.cd{border:1px solid #dde3ed;background:#f4f6fb;}
    tbody tr:nth-child(even) .cs,.ce{background:#f7f9fd;}
    .bot{display:flex;justify-content:space-between;border-top:2px solid #1a3a6b;padding-top:3mm;font-size:9pt;}
    .bc{font-weight:bold;color:#1a3a6b;font-size:12pt;}.bw{color:#777;}
    @page{size:A4 landscape;margin:0;}
    @media print{.page{page-break-after:always;}.page:last-child{page-break-after:avoid;}}
    </style></head><body>${pagesHtml}
    <script>window.onload=()=>{setTimeout(()=>window.print(),400);};</script></body></html>`;

    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (!win) {
      const a = document.createElement('a'); a.href = url; a.download = `emploi-${dateRange.replace(/\//g, '-')}.html`; a.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 15000);
    setPdfLoading(false);
    showMsg('success', 'Prêt — Ctrl+P puis "Enregistrer en PDF"', 5000);
  };

  const toggleCours = (id) => setSelectedCours(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  if (loading) return (
    <div className="container"><Sidebar onLogout={() => { localStorage.removeItem('token'); window.location.href = '/'; }} /><div className="loading">Chargement...</div></div>
  );

  const btnStyle = (bg, disabled) => ({
    backgroundColor: disabled ? '#9ca3af' : bg, color: '#fff', border: 'none',
    padding: '8px 14px', borderRadius: '6px', cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px'
  });

  return (
    <div className="container">
      <Sidebar onLogout={() => { localStorage.removeItem('token'); window.location.href = '/'; }} />

      <div className="header">
        {userInfo && (
          <div className="user-info">
            <div className="user-info-name">{userInfo.nom}</div>
            <div className="user-info-role">{permissions.isGeneral ? 'Général' : `Filière ${userInfo.filiere}`}</div>
          </div>
        )}
        <h1><Calendar size={24} /> Emploi du Temps</h1>
      </div>

      <div className="controls">
        <div className={`permissions-info ${permissions.isGeneral ? 'general' : 'specific'}`}>
          <div className="permissions-details">{permissions.isGeneral ? 'Accès à toutes les filières' : `Filière ${userInfo?.filiere}`}</div>
        </div>

        <div className="cours-selection">
          <h3>Sélectionner les classes :</h3>
          <div className="cours-grid">
            {coursList.map(cours => (
              <div key={cours._id} className={`cours-card ${selectedCours.includes(cours._id) ? 'selected' : ''}`} onClick={() => toggleCours(cours._id)}>
                {cours.nom}
              </div>
            ))}
          </div>
        </div>

        <div className="week-navigation">
          <button className="week-button" onClick={() => changeWeek(-1)}><ChevronLeft size={16} /> Précédente</button>
          <div className="week-info">{formatDate(weekDates[0])} - {formatDate(weekDates[6])}</div>
          <button className="week-button" onClick={() => changeWeek(1)}>Suivante <ChevronRight size={16} /></button>

          <button onClick={copierSemainePrecedente} disabled={copyLoading} style={btnStyle('#f59e0b', copyLoading)}>
            <Copy size={15} />{copyLoading ? 'Copie...' : 'Copier Semaine -1'}
          </button>

          <button onClick={telechargerPDF} disabled={pdfLoading} style={btnStyle('#dc2626', pdfLoading)}>
            <Download size={15} />{pdfLoading ? 'Génération...' : 'Télécharger PDF'}
          </button>

          <button className="week-button history" onClick={() => { setSelectedSeanceForHistory(null); setShowHistorique(true); }}>
            <Clock size={16} /> Historique
          </button>
        </div>
      </div>

      {message.text && <div className={`message ${message.type}`}>{message.text}</div>}

      {/* ── PANNEAU CRÉNEAUX GLOBAUX ── */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', marginBottom: '16px', overflow: 'hidden' }}>
        <button onClick={() => showGlobalPanel ? closeGlobalPanel() : openGlobalPanel()}
          style={{ width: '100%', padding: '12px 16px', background: '#f9fafb', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Clock size={16} />
            <span>Créneaux globaux (toutes les classes)</span>
          </div>
          {showGlobalPanel ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>

        {showGlobalPanel && (
          <div style={{ padding: '16px', borderTop: '1px solid #e5e7eb' }}>
            {/* Onglets jours */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', overflowX: 'auto', paddingBottom: '4px' }}>
              {JOURS_ABR.map((j, i) => (
                <button key={j} onClick={() => setGlobalJourActif(JOURS[i])}
                  style={{ padding: '6px 12px', background: globalJourActif === JOURS[i] ? '#6366f1' : '#f3f4f6', color: globalJourActif === JOURS[i] ? '#fff' : '#666', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                  {j} ({(globalTemp[JOURS[i]] || []).length})
                </button>
              ))}
            </div>

            {/* Liste créneaux */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}>
              {(globalTemp[globalJourActif] || []).length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#bbb', fontSize: '13px' }}>Aucun créneau</div>
              ) : (globalTemp[globalJourActif] || []).map((c, i) => (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#f8f9fb', borderRadius: '6px', padding: '8px' }}>
                  <span style={{ fontSize: '11px', color: '#aaa', fontWeight: '600', minWidth: '18px' }}>{i + 1}</span>
                  <input type="time" value={c.debut} onChange={e => updateGlobal(c.id, 'debut', e.target.value)}
                    style={{ flex: 1, border: '1px solid #e0e0e0', borderRadius: '4px', padding: '4px 6px', fontSize: '12px' }} />
                  <span style={{ color: '#ccc' }}>→</span>
                  <input type="time" value={c.fin} onChange={e => updateGlobal(c.id, 'fin', e.target.value)}
                    style={{ flex: 1, border: '1px solid #e0e0e0', borderRadius: '4px', padding: '4px 6px', fontSize: '12px' }} />
                  <span style={{ fontSize: '11px', color: '#888', minWidth: '60px', textAlign: 'center' }}>{label(c.debut, c.fin)}</span>
                  <button onClick={() => removeGlobal(c.id)}
                    style={{ background: '#fee2e2', border: 'none', borderRadius: '4px', width: '24px', height: '24px', cursor: 'pointer', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>

            {/* Boutons */}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
              <button onClick={addGlobal}
                style={{ padding: '7px 14px', background: 'none', border: '1.5px dashed #6366f1', borderRadius: '6px', color: '#6366f1', fontWeight: '600', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Plus size={13} /> Ajouter
              </button>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={closeGlobalPanel}
                  style={{ padding: '7px 14px', background: '#f3f4f6', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', color: '#555' }}>
                  Annuler
                </button>
                <button onClick={saveGlobalPanel}
                  style={{ padding: '7px 14px', background: '#6366f1', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Save size={13} /> Sauvegarder
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── TABLEAUX PAR CLASSE ── */}
      {selectedCours.map(coursId => {
        const cours = coursList.find(c => c._id === coursId);
        const creneauxDuCours = getCreneauxPourCours(coursId);
        const hasSpecific = !!creneauxSpecifiques[coursId];
        if (!cours) return null;

        const tousLesCreneaux = [];
        Object.values(creneauxDuCours).forEach(arr => arr.forEach(c => {
          if (!tousLesCreneaux.find(x => x.debut === c.debut && x.fin === c.fin)) tousLesCreneaux.push({ ...c });
        }));
        tousLesCreneaux.sort((a, b) => a.debut.localeCompare(b.debut));

        const isOpen = showCoursPanel === coursId;

        return (
          <div key={coursId} style={{ marginBottom: '20px' }}>
            {/* Barre classe + panneau créneaux */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', marginBottom: '10px', overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f9fafb' }}>
                <div>
                  <strong style={{ color: '#1f2937', fontSize: '14px' }}>{cours.nom}</strong>
                  <span style={{ marginLeft: '10px', fontSize: '11px', color: hasSpecific ? '#8b5cf6' : '#9ca3af' }}>
                    {hasSpecific ? 'Créneaux spécifiques' : 'Créneaux globaux'}
                  </span>
                </div>
                <button onClick={() => isOpen ? closeCoursPanel() : openCoursPanel(coursId)}
                  style={{ padding: '6px 11px', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Settings size={12} /> {isOpen ? 'Fermer' : 'Configurer'}
                </button>
              </div>

              {/* Panneau créneaux spécifiques */}
              {isOpen && (
                <div style={{ padding: '16px', borderTop: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', overflowX: 'auto', paddingBottom: '4px' }}>
                    {JOURS_ABR.map((j, i) => (
                      <button key={j} onClick={() => setCoursJourActif(JOURS[i])}
                        style={{ padding: '6px 12px', background: coursJourActif === JOURS[i] ? '#8b5cf6' : '#f3f4f6', color: coursJourActif === JOURS[i] ? '#fff' : '#666', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                        {j} ({(coursTemp[JOURS[i]] || []).length})
                      </button>
                    ))}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}>
                    {(coursTemp[coursJourActif] || []).length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#bbb', fontSize: '13px' }}>Aucun créneau</div>
                    ) : (coursTemp[coursJourActif] || []).map((c, i) => (
                      <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#f8f9fb', borderRadius: '6px', padding: '8px' }}>
                        <span style={{ fontSize: '11px', color: '#aaa', fontWeight: '600', minWidth: '18px' }}>{i + 1}</span>
                        <input type="time" value={c.debut} onChange={e => updateCours(c.id, 'debut', e.target.value)}
                          style={{ flex: 1, border: '1px solid #e0e0e0', borderRadius: '4px', padding: '4px 6px', fontSize: '12px' }} />
                        <span style={{ color: '#ccc' }}>→</span>
                        <input type="time" value={c.fin} onChange={e => updateCours(c.id, 'fin', e.target.value)}
                          style={{ flex: 1, border: '1px solid #e0e0e0', borderRadius: '4px', padding: '4px 6px', fontSize: '12px' }} />
                        <span style={{ fontSize: '11px', color: '#888', minWidth: '60px', textAlign: 'center' }}>{label(c.debut, c.fin)}</span>
                        <button onClick={() => removeCours(c.id)}
                          style={{ background: '#fee2e2', border: 'none', borderRadius: '4px', width: '24px', height: '24px', cursor: 'pointer', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
                    <button onClick={addCours}
                      style={{ padding: '7px 14px', background: 'none', border: '1.5px dashed #8b5cf6', borderRadius: '6px', color: '#8b5cf6', fontWeight: '600', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Plus size={13} /> Ajouter
                    </button>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={closeCoursPanel}
                        style={{ padding: '7px 14px', background: '#f3f4f6', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', color: '#555' }}>
                        Annuler
                      </button>
                      <button onClick={saveCoursPanel}
                        style={{ padding: '7px 14px', background: '#8b5cf6', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Save size={13} /> Sauvegarder
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Tableau */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#1a3a6b', color: 'white' }}>
                      <th style={{ padding: '10px', textAlign: 'center', fontSize: '11px', width: '80px' }}>Horaires</th>
                      {JOURS.map((jour, i) => (
                        <th key={jour} style={{ padding: '10px', textAlign: 'center', fontSize: '11px' }}>
                          {jour}<br /><small style={{ fontWeight: 'normal', fontSize: '10px' }}>{formatDate(weekDates[i])}</small>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tousLesCreneaux.length === 0 ? (
                      <tr><td colSpan={8} style={{ textAlign: 'center', padding: '30px', color: '#bbb', fontSize: '13px' }}>
                        Aucun créneau — cliquez sur "Configurer"
                      </td></tr>
                    ) : tousLesCreneaux.map(creneau => (
                      <tr key={`${creneau.debut}-${creneau.fin}`}>
                        <td style={{ padding: '8px', textAlign: 'center', backgroundColor: '#dce6f5', borderRight: '2px solid #b0bdd4', fontSize: '11px', color: '#1a3a6b', fontWeight: '700', verticalAlign: 'middle' }}>
                          <div>{label(creneau.debut, creneau.fin)}</div>
                          <div style={{ fontSize: '9px', color: '#666', marginTop: '2px' }}>{creneau.debut} - {creneau.fin}</div>
                        </td>
                        {JOURS.map(jour => {
                          const key = `${jour}-${creneau.debut}-${creneau.fin}`;
                          const sd = emploiDuTemps[coursId]?.[key];
                          const crEx = creneauxDuCours[jour]?.some(c => c.debut === creneau.debut && c.fin === creneau.fin);
                          const aSeance = sd && sd.actif !== false;
                          const isEditing = editing?.coursId === coursId && editing?.key === key;

                          return (
                            <td key={jour} style={{ padding: '8px', border: '1px solid #dde3ed', verticalAlign: 'top', backgroundColor: !crEx && !aSeance ? '#f8f9fc' : '#fff', fontSize: '11px', minWidth: '90px' }}>
                              {aSeance && !isEditing && (
                                <div>
                                  <div style={{ fontWeight: '700', color: '#111', fontSize: '11px', marginBottom: '2px' }}>{sd.matiere}</div>
                                  {sd.salle && <div style={{ color: '#1a3a6b', fontSize: '10px', marginBottom: '2px' }}>{sd.salle}</div>}
                                  <div style={{ color: '#666', fontSize: '10px', fontStyle: 'italic', marginBottom: '6px' }}>
                                    {profList.find(p => p._id === sd.professeur)?.nom}
                                  </div>
                                  <div style={{ display: 'flex', gap: '3px' }}>
                                    <button onClick={() => startEdit(coursId, jour, creneau)}
                                      style={{ flex: 1, padding: '3px', background: '#dbeafe', color: '#1e40af', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '9px', fontWeight: '600' }}>
                                      Modifier
                                    </button>
                                    <button onClick={() => deleteSeance(coursId, jour, creneau, sd)}
                                      style={{ flex: 1, padding: '3px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '9px', fontWeight: '600' }}>
                                      Supprimer
                                    </button>
                                  </div>
                                </div>
                              )}

                              {isEditing && (
                                <div>
                                  <select value={emploiDuTemps[coursId]?.[key]?.professeur || ''} onChange={e => updateCase(coursId, jour, creneau, 'professeur', e.target.value)}
                                    style={{ width: '100%', padding: '4px', fontSize: '10px', marginBottom: '4px', border: '1px solid #d1d5db', borderRadius: '3px' }}>
                                    <option value="">Professeur</option>
                                    {getProfesseursPourCours(coursId).map(p => <option key={p._id} value={p._id}>{p.nom}</option>)}
                                  </select>
                                  {emploiDuTemps[coursId]?.[key]?.professeur && (() => {
                                    const mats = getMatieresPourProf(emploiDuTemps[coursId][key].professeur, coursId);
                                    return mats.length > 0 ? (
                                      <select value={emploiDuTemps[coursId]?.[key]?.matiere || ''} onChange={e => updateCase(coursId, jour, creneau, 'matiere', e.target.value)}
                                        style={{ width: '100%', padding: '4px', fontSize: '10px', marginBottom: '4px', border: '1px solid #d1d5db', borderRadius: '3px' }}>
                                        <option value="">Matière</option>
                                        {mats.map(m => <option key={m} value={m}>{m}</option>)}
                                      </select>
                                    ) : (
                                      <input placeholder="Matière" value={emploiDuTemps[coursId]?.[key]?.matiere || ''} onChange={e => updateCase(coursId, jour, creneau, 'matiere', e.target.value)}
                                        style={{ width: '100%', padding: '4px', fontSize: '10px', marginBottom: '4px', border: '1px solid #d1d5db', borderRadius: '3px' }} />
                                    );
                                  })()}
                                  <input placeholder="Salle" value={emploiDuTemps[coursId]?.[key]?.salle || ''} onChange={e => updateCase(coursId, jour, creneau, 'salle', e.target.value)}
                                    style={{ width: '100%', padding: '4px', fontSize: '10px', marginBottom: '4px', border: '1px solid #d1d5db', borderRadius: '3px' }} />
                                  <button onClick={() => saveEdit(coursId, jour, creneau)}
                                    style={{ width: '100%', padding: '4px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '9px', fontWeight: '600' }}>
                                    Valider
                                  </button>
                                  <button onClick={cancelEdit}
                                    style={{ width: '100%', padding: '4px', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '9px', fontWeight: '600', marginTop: '2px' }}>
                                    Annuler
                                  </button>
                                </div>
                              )}

                              {!aSeance && !isEditing && crEx && (
                                <button onClick={() => startEdit(coursId, jour, creneau)}
                                  style={{ width: '100%', padding: '6px', background: '#dbeafe', border: '2px dashed #93c5fd', borderRadius: '4px', color: '#1e40af', cursor: 'pointer', fontSize: '10px', fontWeight: '600' }}>
                                  + Ajouter
                                </button>
                              )}

                              {!aSeance && !isEditing && !crEx && (
                                <div style={{ textAlign: 'center', color: '#d1d5db', fontSize: '12px' }}>—</div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })}

      {selectedCours.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">📅</div>
          <div className="empty-state-title">Sélectionnez une classe</div>
        </div>
      )}

      <HistoriqueModal show={showHistorique} onClose={() => setShowHistorique(false)} seanceId={selectedSeanceForHistory} />
    </div>
  );
};

export default EmploiPedagogique;